import { useState } from 'react';
import cn from 'classnames';

import Link from '@/components/renderers/Link';
import RemixArrowDownLineSVG from '@/svg/icons/remix-arrow-down-line.svg';
import styles from './item.module.css';

export type Content = ({ isDesktop }: { isDesktop: boolean }) => JSX.Element;

export type NavProps = {
  label: string;
  href?: string;
  content?: Content;
  onMouseEnter?: () => void;
  dropdownOpen?: boolean;
  isDesktop?: boolean;
  className?: string;
};

export default function NavItem({
  label,
  content,
  href,
  onMouseEnter,
  dropdownOpen,
  isDesktop,
  className,
}: NavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hasSubItems = !!content;
  const DropdownContent = hasSubItems ? (content as Content) : () => <></>;

  // toggleMenu is for mobile
  const toggleMenu = () => setMenuOpen((open) => !open);

  const linkClass = cn(
    `pb-6 lg:pb-0 pl-2 pr-3 lg:px-0 font-bold text-current cursor-pointer lg:text-sm flex items-center justify-between w-full lg:w-auto focus:outline-none transition-colors`,
    {
      'lg:block relative hover:text-rose': !hasSubItems,
      'cursor-default': hasSubItems,
      'lg:!text-rose': dropdownOpen,
    },
    className,
  );

  return (
    <div>
      {hasSubItems ? (
        <>
          <button
            type="button"
            className={linkClass}
            onClick={toggleMenu}
            aria-label={label}
            aria-haspopup="true"
            aria-expanded={menuOpen || dropdownOpen}
            onMouseEnter={isDesktop ? onMouseEnter : undefined}
          >
            <span>{label}</span>
            <RemixArrowDownLineSVG
              className={cn(
                `hidden lg:inline-block h-4 fill-current lg:ml-1 lg:mr-2`,
                {
                  'transform rotate-180 md:rotate-0': menuOpen,
                },
              )}
            />

            <svg
              width="13"
              height="8"
              viewBox="0 0 13 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cn(`lg:hidden h-4 lg:ml-1 lg:mr-2`, {
                'transform rotate-180 md:rotate-0': menuOpen,
              })}
            >
              <path
                d="M1.5 1.5L6.5 6.5L11.5 1.5"
                stroke="#626E99"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div
            className={cn(
              styles.dropdown,
              dropdownOpen && styles.open,
              menuOpen && `border-catskill-white pb-5 lg:pb-0`,
              `lg:pt-8 lg:border-none mb-5 lg:mb-0 border-b border-transparent`,
            )}
          >
            {(dropdownOpen || menuOpen) && (
              <DropdownContent isDesktop={isDesktop || false} />
            )}
          </div>
        </>
      ) : (
        <Link href={href} className={linkClass}>
          {label}
        </Link>
      )}
    </div>
  );
}
